/* eslint-disable @typescript-eslint/dot-notation */
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NbDialogService, NbCardModule, NbButtonModule, NbIconModule, NbRouteTabsetModule } from '@nebular/theme';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { CONTRIBUTORS, MODE } from 'src/app/shared/constants/general-constants';
import { SpinnerState } from 'src/app/shared/ui/spinner/redux/spinner.state';
import { drbMetadata } from 'src/app/shared/utils/drb-metadata';
import { ENVIRONMENT } from 'src/environments/environment';
import { NewDrbComponent } from '../new-drb/new-drb.component';
import { DrbState, LoadDrb, LoadDrbSuccess } from '../redux';
import { DrbService } from '../services/drb.service';
import { ManageContributorsComponent } from './manage-contributors/manage-contributors.component';
import { NgIf, NgFor } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-drb-sources',
    templateUrl: './drb-sources.component.html',
    styleUrls: ['./drb-sources.component.css'],
    imports: [NgIf, SharedModule, NbCardModule, NgSelectComponent, FormsModule, NgFor, NgOptionComponent, NbButtonModule, NbIconModule, NbRouteTabsetModule]
})
export class DrbSourcesComponent implements OnInit, OnDestroy {
  public drb$: Observable<any[]> = inject(Store).select(DrbState.drb);
  public xliff$: Observable<any[]> = inject(Store).select(DrbState.xliff);
  public file$: Observable<any[]> = inject(Store).select(DrbState.sourceFiles);
  public image$: Observable<any[]> = inject(Store).select(DrbState.sourceImages);
  public link$: Observable<any[]> = inject(Store).select(DrbState.sourceLinks);
  public video$: Observable<any[]> = inject(Store).select(DrbState.sourceVideos);
  public version$: Observable<any[]> = inject(Store).select(DrbState.versions);
  public loading$: Observable<boolean> = inject(Store).select(SpinnerState.isLoading);
  public deploying$: Observable<boolean> = inject(Store).select(DrbState.deploying);
  public noDrbDraft$: Observable<boolean> = inject(Store).select(DrbState.noDrbDraft);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  public spin;
  public displayVersionButton;
  public mode;
  public metaData;
  public resourceData;
  public xliff;
  public sourceFiles;
  public sourceImages;
  public sourceLinks;
  public sourceVideos;
  public username;
  public versions: any[];
  public versionSelected;
  public draftVersion;
  public ownerOrContributor;
  public deployInProgres;
  public environment: string = ENVIRONMENT.name;
  public bundle_name: string;
  public old_bundle_name: string;

  public sourceTabs: any[] = [
    {
      title: $localize`Strings`,
      route: './strings'
    },
    {
      title: $localize`Files`,
      route: './files'
    },
    {
      title: $localize`Images`,
      route: './images'
    },
    {
      title: $localize`Links`,
      route: './links'
    },
    {
      title: $localize`Videos`,
      route: './videos'
    }
  ];

  constructor(
    private router: Router,
    private service: DrbService,
    private route: ActivatedRoute,
    private store: Store,
    private action$: Actions,
    public utility: UtilityService,
    private dialogService: NbDialogService
  ) {}

  public ngOnInit() {
    this.detectRouteChanges();
    this.mode = MODE.CREATE;
    this.route.url.pipe(takeUntil(this.unsubscribe)).subscribe((event) => (this.bundle_name = event[1].path));
    this.old_bundle_name = this.bundle_name;
    this.store.dispatch(new LoadDrb(this.bundle_name));
    this.drb$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (!Array.isArray(data)) {
        this.set_metadata(data);
        this.ownerOrContributor = this.checkOwnerOrContributor();
        this.routeToPage();
      }
    });
    this.noDrbDraft$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.metaData = null;
        this.dialogService.open(NewDrbComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            bundle_name: this.bundle_name
          }
        });
      }
    });
    this.xliff$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.xliff = data));
    this.file$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceFiles = data));
    this.image$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceImages = data));
    this.link$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceLinks = data));
    this.video$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceVideos = data));
    this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.versions = data));
    this.loading$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.spin = data));
    this.deploying$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.deployInProgres = data));
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));

    this.displayVersionButton = false;

    this.action$.pipe(ofActionCompleted(LoadDrbSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.ownerOrContributor = this.checkOwnerOrContributor();
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public onChange(event) {
    return;
  }

  public manageContributors(): void {
    this.dialogService.open(ManageContributorsComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        contributors: JSON.parse(JSON.stringify(this.metaData[CONTRIBUTORS])),
        username: this.username,
        bundle_name: this.bundle_name
      }
    });
  }

  public checkOwnerOrContributor() {
    if (this.username === this.metaData.owner || this.metaData.contributors.includes(this.username)) {
      return true;
    }
  }

  public reload() {
    this.store.dispatch(new LoadDrb(this.bundle_name));
  }

  public set_metadata(data) {
    this.metaData = drbMetadata(data);
  }

  public displayContributors() {
    const contributors = JSON.parse(JSON.stringify(this.metaData[CONTRIBUTORS])).filter(
      (element) => element.length > 0
    );

    return contributors.join(', ');
  }

  public routeToPage() {
    if (this.router.url.includes('drb/' + this.bundle_name + '/create')) {
      if (this.router.url.endsWith('files')) {
        this.router.navigate(['/drb/' + this.bundle_name + '/create/files']);
      } else if (this.router.url.endsWith('images')) {
        this.router.navigate(['/drb/' + this.bundle_name + '/create/images']);
      } else if (this.router.url.endsWith('links')) {
        this.router.navigate(['/drb/' + this.bundle_name + '/create/links']);
      } else if (this.router.url.endsWith('videos')) {
        this.router.navigate(['/drb/' + this.bundle_name + '/create/videos']);
      } else {
        this.router.navigate(['/drb/' + this.bundle_name + '/create/strings']);
      }
    }
  }

  private detectRouteChanges(): void {
    this.router.events.pipe(takeUntil(this.unsubscribe)).subscribe((event) => {
      if (this.old_bundle_name !== this.bundle_name && event instanceof NavigationEnd) {
        this.old_bundle_name = this.bundle_name;
        this.store.dispatch(new LoadDrb(this.bundle_name));
      }
    });
  }
}
